import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Shield, Truck, Wrench, Star, ChevronRight, History, Play } from 'lucide-react';
import { getPublicApiUrl } from '@/lib/publicApi';

const API_URL = getPublicApiUrl();

export default function PublicHomePage() {
  const { settings } = useOutletContext() || {};
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/machines`);
      const data = await res.json();
      setMachines(data.slice(0, 4)); // Show first 4 machines
    } catch (error) {
      console.error('Failed to fetch machines:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Shield, title: 'Fiabilité testée', description: 'Machines robustes, éprouvées sur terrain et prêtes pour les gros chantiers.' },
    { icon: Wrench, title: 'SAV & Accompagnement', description: 'Pièces disponibles, support local, conseils d\'utilisation.' },
    { icon: Truck, title: 'Livraison rapide', description: 'Votre machine livrée prête à travailler sur tout l\'océan indien.' },
    { icon: Star, title: 'Prix compétitifs', description: 'Un rapport qualité/prix imbattable sur l\'île.' },
  ];

  const stats = [
    { value: '100+', label: 'Mini-pelles livrées' },
    { value: '98%', label: 'Clients satisfaits' },
    { value: '24H', label: 'SAV réactif' },
  ];

  const testimonials = [
    { 
      text: "Service nickel du début à la fin. Mini-pelle livrée rapidement, bien expliquée, et surtout un SAV qui répond vraiment.", 
      author: "Jean-Marc", 
      location: "Saint-Louis" 
    },
    { 
      text: "Matériel solide, prix corrects et accompagnement sérieux. Ils m'ont conseillé le bon modèle pour mon activité, aucun regret.", 
      author: "Kevin", 
      location: "Sainte-Marie" 
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzLTItMi0yLTQgMi00IDItNCAyIDIgMiA0LTIgNC0yIDQgMiAyIDIgNHoiLz48L2c+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        {settings?.hero_image_url && (
          <div 
            className="absolute inset-0 opacity-30 bg-cover bg-center"
            style={{ backgroundImage: `url(${settings.hero_image_url.startsWith('/api/') ? API_URL + settings.hero_image_url : settings.hero_image_url})` }}
          ></div>
        )}
        <div className="max-w-7xl mx-auto px-4 py-20 md:py-32 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {/* Badges principaux */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-full text-sm font-medium">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                  {settings?.site_tagline || 'Importateur à La Réunion'}
                </div>
                {/* Badge Caterpillar mis en évidence */}
                <Link 
                  to="/portail/histoire-liugong"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg shadow-yellow-500/30 hover:scale-105 transition-transform"
                >
                  <span className="bg-white text-yellow-600 px-1.5 py-0.5 rounded text-xs font-black">CAT®</span>
                  Licence Caterpillar 1987
                </Link>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                {settings?.hero_title || 'Mini-Pelles Professionnelles'}
                <span className="text-yellow-500"> pour Particuliers & Entreprises</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                {settings?.hero_subtitle || 'Votre machine livrée et prête à travailler sur tout l\'océan indien ! Des machines fiables, puissantes et accessibles.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/portail/machines"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition shadow-lg shadow-yellow-500/25"
                >
                  Voir nos modèles
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/portail/configurateur"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition backdrop-blur"
                >
                  {settings?.cta_text || 'Demander un devis'}
                </Link>
              </div>
            </div>
            <div className="relative hidden md:block">
              <div className="absolute -inset-4 bg-yellow-500/20 rounded-3xl blur-3xl"></div>
              {settings?.hero_machine_image_url ? (
                <img 
                  src={settings.hero_machine_image_url.startsWith('/api/') ? API_URL + settings.hero_machine_image_url : settings.hero_machine_image_url}
                  alt="Mini-pelle IDMAT OI" 
                  className="relative w-full max-w-lg mx-auto drop-shadow-2xl rounded-2xl"
                />
              ) : machines.length > 0 && machines[0].image_url ? (
                <img 
                  src={machines[0].image_url.startsWith('/api/') ? API_URL + machines[0].image_url : machines[0].image_url}
                  alt="Mini-pelle IDMAT OI" 
                  className="relative w-full max-w-lg mx-auto drop-shadow-2xl rounded-2xl"
                />
              ) : (
                <div className="relative w-full max-w-lg mx-auto aspect-square bg-slate-800/50 rounded-2xl flex items-center justify-center">
                  <Wrench className="w-24 h-24 text-yellow-500/50" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              Pourquoi Choisir <span className="text-yellow-500">Nos Mini-Pelles</span> ?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Des machines conçues pour performer sur tous vos chantiers : terrassement, VRD, fondations, piscines, paysagisme...
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-50 rounded-2xl hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-yellow-500/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-yellow-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Machines Preview Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                Notre <span className="text-yellow-500">Gamme</span>
              </h2>
              <p className="text-slate-600">Mini-pelles, excavatrices, chargeuses compactes & chariots élévateurs</p>
            </div>
            <Link 
              to="/portail/machines"
              className="inline-flex items-center gap-2 text-yellow-600 font-medium hover:text-yellow-700 transition"
            >
              Voir toutes les machines
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-500">Chargement...</div>
          ) : machines.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Nos machines seront bientôt disponibles. Contactez-nous pour plus d'informations.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {machines.map((machine) => (
                <Link 
                  key={machine.id}
                  to={`/portail/machines/${machine.id}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                    {machine.image_url ? (
                      <img 
                        src={machine.image_url.startsWith('/api/') ? `${API_URL}${machine.image_url}` : machine.image_url} 
                        alt={machine.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <Wrench className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    {machine.brand && (
                      <span className="text-xs font-medium text-yellow-600 uppercase tracking-wider">{machine.brand}</span>
                    )}
                    <h3 className="text-lg font-semibold text-slate-800 mt-1">{machine.name}</h3>
                    {machine.weight && (
                      <p className="text-sm text-slate-500 mt-1">{machine.weight}</p>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm text-yellow-600 font-medium group-hover:text-yellow-700 transition flex items-center gap-1">
                        Voir détails <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link 
              to="/portail/configurateur"
              className="inline-flex items-center gap-3 px-8 py-4 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition"
            >
              <span>🎯</span>
              Souhaitez-vous découvrir une de nos machines ?
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {settings?.show_stats !== false && (
        <section className="py-16 bg-yellow-500">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-3 gap-8 text-center">
              {stats.map((stat, index) => (
                <div key={index}>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    {stat.value}
                  </div>
                  <div className="text-yellow-100">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {settings?.show_testimonials !== false && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                Ce Que Disent <span className="text-yellow-500">Nos Clients</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className="p-8 bg-slate-50 rounded-2xl"
                >
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-lg text-slate-700 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <span className="text-yellow-600 font-semibold">{testimonial.author.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{testimonial.author}</p>
                      <p className="text-sm text-slate-500">{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LiuGong History Banner */}
      <section className="py-16 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Logo LiuGong avec badge Caterpillar */}
            <div className="flex-shrink-0 relative">
              <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-yellow-500/30 transform hover:scale-105 transition-transform">
                <span className="text-4xl md:text-5xl font-black text-white">LG</span>
              </div>
              {/* Badge Caterpillar */}
              <div className="absolute -bottom-2 -right-2 bg-yellow-400 text-slate-900 text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
                CAT®
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 text-center md:text-left">
              {/* Badge Caterpillar mis en évidence */}
              <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                Technologie sous licence Caterpillar® depuis 1987
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                Découvrez l'Histoire de{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                  LiuGong
                </span>
              </h2>
              <p className="text-slate-300 mb-6 max-w-xl">
                De 1958 à aujourd'hui, plongez dans l'épopée d'un géant chinois devenu leader mondial. Découvrez comment le transfert de technologie <span className="text-yellow-400 font-semibold">Caterpillar</span> a propulsé LiuGong vers l'excellence internationale.
              </p>
              
              {/* Quick stats avec Caterpillar */}
              <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-6">
                <div className="bg-yellow-500/20 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-500/40">
                  <span className="font-bold text-yellow-400">CAT® 966</span>
                  <span className="text-white/70 ml-1 text-sm">licence</span>
                </div>
                {[
                  { value: '65+', label: 'ans' },
                  { value: '170+', label: 'pays' },
                  { value: 'Top 10', label: 'mondial' }
                ].map((stat, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                    <span className="font-bold text-yellow-400">{stat.value}</span>
                    <span className="text-white/70 ml-1 text-sm">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="flex-shrink-0">
              <Link 
                to="/portail/histoire-liugong"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-yellow-500/30 transition-all transform hover:scale-105"
              >
                <Play className="w-5 h-5" />
                Explorer l'histoire
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
            Prêt à Recevoir Votre Mini-Pelle ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contactez-nous pour un prix personnalisé sur le modèle qui correspond à vos besoins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/portail/devis"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition shadow-lg"
            >
              Demander un devis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a 
              href={`tel:${settings?.contact_phone || '0693526449'}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition backdrop-blur"
            >
              <Phone className="w-5 h-5" />
              {settings?.contact_phone || '0693 52 64 49'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
